import { useAuth, useClerk, useUser } from '@clerk/react';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Skeleton,
  Stack,
  type SvgIconProps,
  TextField,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { CURRENT_TOS_VERSION } from '@prospero/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useSetAtom } from 'jotai';
import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { authQueryKeys } from '../../hooks/auth/use-memberships';
import { useAcceptInvite } from '../../hooks/use-accept-invite';
import { useInviteDetails } from '../../hooks/use-invite';
import { useCreateInviteMagicLink } from '../../hooks/use-invite-mutations';
import { ConsentService } from '../../services/consent/consent.service';
import type { IConsentService } from '../../services/consent/consent.service.interface';
import { HttpService, type IHttpService } from '../../services/http/http.service';
import { InviteError } from '../../services/invite/invite.service';
import { updateTenantContextAtom } from '../../stores/auth.store';
import { colors } from '../../theme/ai-theme';
import { CARD_FLOW_MAX_WIDTH } from '../../theme/page-widths';
import { userNeedsPassword } from '../../utils/user-needs-password';

/** Uses client.signIn directly — Clerk 6.6.0 useSignIn() lacks setActive. */
type PageState =
  | 'loading'
  | 'signing-in'
  | 'wrong-account'
  | 'needs-password'
  | 'success'
  | 'error';
type StepStatus = 'pending' | 'active' | 'done';

function deriveErrorKind(error: unknown): 'expired' | 'notFound' | null {
  if (!(error instanceof InviteError)) {
    return null;
  }
  return error.status === 410 ? 'expired' : 'notFound';
}

function deriveProgressSteps(
  inviteDetails: { email: string } | undefined,
  pageState: PageState,
  isSignedIn: boolean,
  needsPassword: boolean,
): { step1: StepStatus; step2: StepStatus; step3: StepStatus; step4: StepStatus | null } {
  const isPasswordPhase = pageState === 'needs-password';
  const step1: StepStatus = inviteDetails ? 'done' : 'active';
  const step2: StepStatus = !inviteDetails
    ? 'pending'
    : isPasswordPhase || (pageState === 'signing-in' && isSignedIn)
      ? 'done'
      : pageState === 'signing-in'
        ? 'active'
        : 'pending';
  const step3: StepStatus = step2 === 'done' ? (isPasswordPhase ? 'done' : 'active') : 'pending';
  const step4: StepStatus | null = !needsPassword ? null : step3 === 'done' ? 'active' : 'pending';
  return { step1, step2, step3, step4 };
}

function useInviteAcceptFlow(
  token: string | undefined,
  tenantId: string | undefined,
  inviteDetails:
    | { email: string; tenantId?: string; tenantName?: string; roleName?: string }
    | undefined,
  isLoaded: boolean,
  isSignedIn: boolean,
  isLoading: boolean,
  error: unknown,
  currentUserEmail: string | undefined,
) {
  const { t } = useTranslation();
  const clerk = useClerk();
  const { signOut } = clerk;
  const navigate = useNavigate();
  const location = useLocation();
  const acceptInviteMutation = useAcceptInvite();
  const createMagicLinkMutation = useCreateInviteMagicLink();
  const queryClient = useQueryClient();
  const updateTenantContext = useSetAtom(updateTenantContextAtom);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [acceptedTenantName, setAcceptedTenantName] = useState<string | null>(null);

  const handleSignOutAndAccept = useCallback(async () => {
    const returnUrl = `${location.pathname}${location.search}`;
    await signOut({ redirectUrl: returnUrl });
  }, [signOut, location.pathname, location.search]);

  const navTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(navTimerRef.current), []);

  const goToSuccess = useCallback(
    (tenantName: string | null) => {
      if (tenantName !== null) {
        setAcceptedTenantName(tenantName);
      }
      setPageState('success');
      navTimerRef.current = setTimeout(() => navigate('/', { replace: true }), 1500);
    },
    [navigate],
  );

  const finalizeAccept = useCallback(
    (inviteToken: string, inviteTenantId: string) => {
      acceptInviteMutation.mutate(
        { token: inviteToken, tenantId: inviteTenantId },
        {
          onSuccess: (result) => {
            updateTenantContext({ tenantId: result.tenantId, tenantName: result.tenantName });
            // La chiave corretta è ['auth','memberships']: invalidare la
            // letterale ['memberships'] non matchava nulla e il selettore
            // azienda non mostrava il nuovo tenant finché a un reload.
            void queryClient.invalidateQueries({ queryKey: authQueryKeys.memberships() });
            setAcceptedTenantName(result.tenantName);
            const clerkUser = clerk.user;
            if (
              clerkUser &&
              userNeedsPassword({
                passwordEnabled: clerkUser.passwordEnabled,
                externalAccounts: clerkUser.externalAccounts,
              })
            ) {
              setPageState('needs-password');
              return;
            }
            goToSuccess(result.tenantName);
          },
          onError: (err) => {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 410) {
              goToSuccess(null);
              return;
            }
            setPageState('error');
            setAcceptError(t('invite.accept.cannotAccept'));
          },
        },
      );
    },
    [acceptInviteMutation, updateTenantContext, queryClient, clerk, goToSuccess, t],
  );

  const handleMagicLinkSuccess = useCallback(
    async (ticket: string, inviteToken: string, inviteTenantId: string) => {
      const signIn = clerk.client.signIn;
      const result = await signIn.create({ strategy: 'ticket', ticket });
      if (result.status !== 'complete' || !result.createdSessionId) {
        throw new Error(`Sign-in incomplete (status: ${result.status})`);
      }
      await clerk.setActive({ session: result.createdSessionId });
      finalizeAccept(inviteToken, inviteTenantId);
    },
    [clerk, finalizeAccept],
  );

  const handleMagicLinkSuccessRef = useRef(handleMagicLinkSuccess);
  handleMagicLinkSuccessRef.current = handleMagicLinkSuccess;
  const createMagicLinkMutationRef = useRef(createMagicLinkMutation);
  createMagicLinkMutationRef.current = createMagicLinkMutation;

  useEffect(() => {
    if (!isLoaded || isLoading || !inviteDetails || hasAttempted || error || !token || !tenantId) {
      return;
    }

    setHasAttempted(true);

    if (isSignedIn) {
      if (currentUserEmail?.toLowerCase() === inviteDetails.email.toLowerCase()) {
        setPageState('signing-in');
        finalizeAccept(token, tenantId);
      } else {
        setPageState('wrong-account');
      }
    } else {
      setPageState('signing-in');
      createMagicLinkMutationRef.current.mutate(
        { token, tenantId },
        {
          onSuccess: async ({ ticket }) => {
            try {
              await handleMagicLinkSuccessRef.current(ticket, token, tenantId);
            } catch {
              setPageState('error');
              setAcceptError(t('invite.accept.cannotSignIn'));
            }
          },
          onError: () => {
            setPageState('error');
            setAcceptError(t('invite.accept.cannotSignIn'));
          },
        },
      );
    }
  }, [
    isLoaded,
    isSignedIn,
    isLoading,
    error,
    inviteDetails,
    hasAttempted,
    currentUserEmail,
    token,
    tenantId,
    finalizeAccept,
    t,
  ]);

  return {
    pageState,
    acceptError,
    acceptedTenantName,
    handleSignOutAndAccept,
    completePasswordSetup: () => goToSuccess(null),
  };
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? undefined;
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const { data: inviteDetails, isLoading, error } = useInviteDetails(token, tenantId);

  const currentUserEmail = user?.primaryEmailAddress?.emailAddress;

  const {
    pageState,
    acceptError,
    acceptedTenantName,
    handleSignOutAndAccept,
    completePasswordSetup,
  } = useInviteAcceptFlow(
    token,
    tenantId,
    inviteDetails,
    isLoaded,
    isSignedIn ?? false,
    isLoading,
    error,
    currentUserEmail,
  );

  if (pageState === 'success') {
    return (
      <PageShell>
        <SuccessPanel tenantName={acceptedTenantName ?? inviteDetails?.tenantName} />
      </PageShell>
    );
  }

  if (error) {
    const errorKind = deriveErrorKind(error);
    return (
      <PageShell>
        <ErrorPanel
          variant={errorKind ?? 'notFound'}
          onPrimary={() => navigate('/sign-in')}
          primaryLabel={t('invite.accept.goToLogin')}
        />
      </PageShell>
    );
  }

  if (pageState === 'error') {
    return (
      <PageShell>
        <ErrorPanel
          variant="generic"
          customTitle={acceptError ?? t('invite.accept.errorTitle')}
          onPrimary={() => navigate('/sign-in')}
          primaryLabel={t('invite.accept.goToLogin')}
        />
      </PageShell>
    );
  }

  if (pageState === 'wrong-account') {
    return (
      <PageShell>
        <WrongAccountPanel
          inviteEmail={inviteDetails?.email ?? ''}
          currentEmail={currentUserEmail ?? ''}
          onAccept={handleSignOutAndAccept}
          onCancel={() => navigate('/')}
        />
      </PageShell>
    );
  }

  const needsPassword = pageState === 'needs-password';
  const { step1, step2, step3, step4 } = deriveProgressSteps(
    inviteDetails,
    pageState,
    isSignedIn ?? false,
    needsPassword,
  );

  return (
    <PageShell>
      <ProgressPanel
        tenantName={inviteDetails?.tenantName}
        email={inviteDetails?.email}
        roleName={inviteDetails?.roleName}
        step1={step1}
        step2={step2}
        step3={step3}
        step4={step4}
        loadingDetails={!inviteDetails}
        showPasswordForm={needsPassword}
        onPasswordCompleted={completePasswordSetup}
      />
    </PageShell>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 6 },
        overflow: 'hidden',
        background: `
          radial-gradient(ellipse at 20% 20%, ${alpha(colors.primary.main, 0.08)} 0%, transparent 50%),
          radial-gradient(ellipse at 80% 80%, ${alpha(colors.secondary.main, 0.06)} 0%, transparent 50%),
          radial-gradient(ellipse at 50% 0%, ${alpha(colors.primary.main, 0.12)} 0%, transparent 60%),
          ${colors.background.default}
        `,
        '@keyframes invFadeUp': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes invDrawCheck': {
          from: { strokeDashoffset: 28 },
          to: { strokeDashoffset: 0 },
        },
        '@keyframes invPulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(1.06)', opacity: 0.85 },
        },
        '@keyframes invShimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(200%)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: CARD_FLOW_MAX_WIDTH,
          animation: 'invFadeUp 600ms cubic-bezier(0.2, 0.8, 0.2, 1) both',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function HeroCard({ children }: { children: ReactNode }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 4,
        backgroundColor: alpha(theme.palette.background.paper, 0.92),
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: `0 24px 70px -28px ${alpha(theme.palette.primary.dark, 0.45)},
                    0 6px 18px -8px ${alpha(theme.palette.common.black, 0.12)}`,
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 35%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
        }}
      />
      <Box sx={{ position: 'relative' }}>{children}</Box>
    </Box>
  );
}

function ProgressPanel({
  tenantName,
  email,
  roleName,
  step1,
  step2,
  step3,
  step4,
  loadingDetails,
  showPasswordForm,
  onPasswordCompleted,
}: {
  tenantName?: string;
  email?: string;
  roleName?: string;
  step1: StepStatus;
  step2: StepStatus;
  step3: StepStatus;
  step4: StepStatus | null;
  loadingDetails: boolean;
  showPasswordForm: boolean;
  onPasswordCompleted: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <HeroCard>
      <Box sx={{ p: { xs: 3.5, sm: 5 } }}>
        <Stack spacing={3.5}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: theme.palette.primary.contrastText,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 8px 20px -6px ${alpha(theme.palette.primary.main, 0.55)}`,
              }}
            >
              <RestaurantMenuRoundedIcon fontSize="small" />
            </Box>
            <Stack spacing={0.25}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: 1.4,
                  color: 'text.secondary',
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {t('invite.accept.eyebrow')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.muted', lineHeight: 1.2 }}>
                Prospero · HACCP
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Typography
              component="div"
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.6rem', sm: '2.05rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {t('invite.accept.heroPrefix')}
              </Box>{' '}
              {loadingDetails ? (
                <Skeleton
                  variant="text"
                  width="60%"
                  sx={{ display: 'inline-block', verticalAlign: 'middle' }}
                />
              ) : (
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(95deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {tenantName ?? t('invite.accept.heroFallback')}
                </Box>
              )}
            </Typography>
          </Stack>

          {(email || roleName || loadingDetails) && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {loadingDetails ? (
                <>
                  <Skeleton variant="rounded" width={180} height={32} />
                  <Skeleton variant="rounded" width={120} height={32} />
                </>
              ) : (
                <>
                  {email && (
                    <Chip
                      icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
                      label={email}
                      size="small"
                      sx={{
                        height: 32,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.dark,
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: theme.palette.primary.dark, ml: 0.5 },
                      }}
                    />
                  )}
                  {roleName && (
                    <Chip
                      icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />}
                      label={t('invite.accept.roleChip', { role: roleName })}
                      size="small"
                      sx={{
                        height: 32,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                        color: theme.palette.secondary.dark,
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: theme.palette.secondary.dark, ml: 0.5 },
                      }}
                    />
                  )}
                </>
              )}
            </Stack>
          )}

          <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.06) }} />

          {showPasswordForm ? (
            <CompactStepper statuses={[step1, step2, step3, step4 ?? 'pending']} currentIndex={3} />
          ) : (
            <Stack spacing={1.25}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 1.2, fontWeight: 600, color: 'text.muted' }}
              >
                {t('invite.accept.stepsHeading')}
              </Typography>
              <Stack spacing={0}>
                <ProgressStep
                  index={1}
                  status={step1}
                  title={t('invite.accept.step1Title')}
                  doneLabel={t('invite.accept.step1Done')}
                  showConnector
                  connectorActive={step1 === 'done'}
                />
                <ProgressStep
                  index={2}
                  status={step2}
                  title={t('invite.accept.step2Title')}
                  doneLabel={t('invite.accept.step2Done')}
                  showConnector
                  connectorActive={step2 === 'done'}
                />
                <ProgressStep
                  index={3}
                  status={step3}
                  title={t('invite.accept.step3Title')}
                  doneLabel={t('invite.accept.step3Done')}
                  showConnector={!!step4}
                  connectorActive={step3 === 'done'}
                />
                {step4 && (
                  <ProgressStep
                    index={4}
                    status={step4}
                    title={t('invite.accept.step4Title')}
                    doneLabel={t('invite.accept.step4Done')}
                  />
                )}
              </Stack>
            </Stack>
          )}

          {showPasswordForm ? (
            <PasswordSetupSection onCompleted={onPasswordCompleted} />
          ) : (
            <Box
              sx={{
                borderRadius: 2.5,
                p: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`,
              }}
            >
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
                <StorefrontRoundedIcon
                  fontSize="small"
                  sx={{ color: theme.palette.warning.dark, mt: 0.25 }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                  {t('invite.accept.reassurance')}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </HeroCard>
  );
}

const COMPACT_STEP_KEYS = ['step-1', 'step-2', 'step-3', 'step-4'] as const;

function CompactStepper({
  statuses,
  currentIndex,
}: {
  statuses: readonly StepStatus[];
  currentIndex: number;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const total = statuses.length;
  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          letterSpacing: 1.2,
          fontWeight: 700,
          color: 'primary.dark',
          lineHeight: 1.2,
          flexShrink: 0,
        }}
      >
        {t('invite.accept.compactProgress', { current: currentIndex + 1, total })}
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: 'center', flexShrink: 0 }}
        aria-hidden
      >
        {statuses.map((status, idx) => {
          const isCurrent = idx === currentIndex;
          const isDone = status === 'done';
          const dotColor = isDone
            ? theme.palette.success.main
            : isCurrent
              ? theme.palette.primary.main
              : alpha(theme.palette.text.primary, 0.18);
          return (
            <Box
              key={COMPACT_STEP_KEYS[idx] ?? `step-${idx}`}
              sx={{
                width: isCurrent ? 22 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: dotColor,
                transition: 'all 280ms ease',
                ...(isCurrent && {
                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}`,
                }),
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

const MIN_PASSWORD_LENGTH = 8;

async function extractApiMessage(err: unknown): Promise<string | null> {
  const response = (err as { response?: Response })?.response;
  if (!response || typeof response.json !== 'function') {
    return null;
  }
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body?.message === 'string' && body.message.trim().length > 0) {
      return body.message;
    }
  } catch {
    // body not JSON or already consumed
  }
  return null;
}

interface InviteSetupFormValues {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  tosAccepted: boolean;
  unfairTermsAccepted: boolean;
  consentPlatformImprovement: boolean;
  consentAnonymizedStatistics: boolean;
}

function PasswordRequirementsList({ currentPassword }: { currentPassword: string }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const meetsLength = currentPassword.length >= MIN_PASSWORD_LENGTH;
  const hasMix =
    /[a-zA-Z]/.test(currentPassword) &&
    (/\d/.test(currentPassword) || /[^a-zA-Z0-9]/.test(currentPassword));

  const items: Array<{ key: string; label: string; satisfied: boolean }> = [
    {
      key: 'minLength',
      label: t('invite.accept.passwordRequirementMinLength'),
      satisfied: meetsLength,
    },
    {
      key: 'mix',
      label: t('invite.accept.passwordRequirementMix'),
      satisfied: hasMix,
    },
  ];

  return (
    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.25 }}>
      {items.map((item) => {
        const color = item.satisfied
          ? theme.palette.success.main
          : alpha(theme.palette.text.primary, 0.45);
        return (
          <Stack key={item.key} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color,
                border: item.satisfied ? 'none' : `1.5px solid ${alpha(color, 0.5)}`,
                backgroundColor: item.satisfied ? color : 'transparent',
                transition: 'all 200ms ease',
              }}
            >
              {item.satisfied && (
                <CheckRoundedIcon
                  sx={{ fontSize: 10, color: theme.palette.success.contrastText }}
                />
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: item.satisfied ? 'success.dark' : 'text.secondary',
                fontWeight: item.satisfied ? 600 : 500,
                lineHeight: 1.4,
                fontSize: '0.7rem',
              }}
            >
              {item.label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

function splitTosLabel(raw: string): { before: string; link: string; after: string } {
  const [beforeRaw, rest = ''] = raw.split('<tosLink>');
  const [linkRaw, afterRaw = ''] = rest.split('</tosLink>');
  return { before: beforeRaw, link: linkRaw, after: afterRaw };
}

function TosSection({
  tosAccepted,
  unfairTermsAccepted,
  onTosChange,
  onUnfairChange,
}: {
  tosAccepted: boolean;
  unfairTermsAccepted: boolean;
  onTosChange: (checked: boolean) => void;
  onUnfairChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const { t: tOnboarding } = useTranslation('onboarding');
  const theme = useTheme();
  const tosLabel = splitTosLabel(tOnboarding('tosAcceptance'));

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            color: theme.palette.warning.contrastText,
            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
          }}
        >
          <GavelRoundedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t('invite.accept.tosHeadline')}
        </Typography>
      </Stack>

      <Stack spacing={0.5}>
        <FormControlLabel
          control={
            <Checkbox
              checked={tosAccepted}
              onChange={(e) => onTosChange(e.target.checked)}
              size="small"
              sx={{ p: 0.5 }}
            />
          }
          label={
            <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
              {tosLabel.before}
              <MuiLink component={RouterLink} to="/terms-of-service" underline="hover">
                {tosLabel.link}
              </MuiLink>
              {tosLabel.after}
            </Typography>
          }
          sx={{ alignItems: 'flex-start', mx: 0 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={unfairTermsAccepted}
              onChange={(e) => onUnfairChange(e.target.checked)}
              size="small"
              sx={{ p: 0.5 }}
            />
          }
          label={
            <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
              {tOnboarding('unfairTermsShort')}
            </Typography>
          }
          sx={{ alignItems: 'flex-start', mx: 0 }}
        />
      </Stack>
    </Stack>
  );
}

function PasswordSetupSection({ onCompleted }: { onCompleted: () => void }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useUser();
  const http = useInjection<IHttpService>(HttpService);
  const consentService = useInjection<IConsentService>(ConsentService);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<InviteSetupFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      tosAccepted: false,
      unfairTermsAccepted: false,
      consentPlatformImprovement: false,
      consentAnonymizedStatistics: false,
    },
    mode: 'onChange',
  });

  const password = watch('password');
  const tosAccepted = watch('tosAccepted');
  const unfairTermsAccepted = watch('unfairTermsAccepted');
  const consentPlatformImprovement = watch('consentPlatformImprovement');
  const consentAnonymizedStatistics = watch('consentAnonymizedStatistics');
  const tosComplete = tosAccepted && unfairTermsAccepted;

  const setupMutation = useMutation({
    mutationFn: (payload: { newPassword: string; firstName: string; lastName: string }) =>
      http.post<void>('account/complete-invite-setup', payload),
  });

  const onSubmit = async (data: InviteSetupFormValues) => {
    setSubmitError(null);
    try {
      await setupMutation.mutateAsync({
        newPassword: data.password,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
      });

      for (const consent of [
        {
          consentType: 'TOS_ACCEPTANCE' as const,
          granted: data.tosAccepted,
          clauseVersion: CURRENT_TOS_VERSION,
        },
        {
          consentType: 'TOS_UNFAIR_TERMS_1341' as const,
          granted: data.unfairTermsAccepted,
          clauseVersion: CURRENT_TOS_VERSION,
        },
        { consentType: 'PLATFORM_IMPROVEMENT' as const, granted: data.consentPlatformImprovement },
        {
          consentType: 'ANONYMIZED_STATISTICS' as const,
          granted: data.consentAnonymizedStatistics,
        },
      ]) {
        await consentService.updateConsent(consent);
      }

      await user?.reload();
      onCompleted();
    } catch (err) {
      const apiMessage = await extractApiMessage(err);
      setSubmitError(apiMessage ?? t('invite.accept.passwordError'));
    }
  };

  const passwordRule = errors.password?.message;
  const confirmRule = errors.confirmPassword?.message;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ position: 'relative' }}>
      <Stack spacing={3}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color: theme.palette.primary.contrastText,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              <PersonOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {t('invite.accept.nameHeadline')}
            </Typography>
          </Stack>

          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4, mt: -1 }}>
            {t('invite.accept.nameHelper')}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }}>
            <TextField
              label={t('invite.accept.firstNameLabel')}
              autoComplete="given-name"
              autoFocus
              fullWidth
              size="small"
              {...register('firstName', {
                required: t('invite.accept.firstNameRequired'),
                validate: (v) => v.trim().length > 0 || t('invite.accept.firstNameRequired'),
              })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <TextField
              label={t('invite.accept.lastNameLabel')}
              autoComplete="family-name"
              fullWidth
              size="small"
              {...register('lastName', {
                required: t('invite.accept.lastNameRequired'),
                validate: (v) => v.trim().length > 0 || t('invite.accept.lastNameRequired'),
              })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.06) }} />

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color: theme.palette.primary.contrastText,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {t('invite.accept.passwordHeadline')}
            </Typography>
          </Stack>

          <Box sx={{ mt: -1 }}>
            <PasswordRequirementsList currentPassword={password} />
          </Box>

          <Stack spacing={2}>
            <TextField
              label={t('invite.accept.passwordFieldLabel')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              fullWidth
              size="small"
              {...register('password', {
                required: t('invite.accept.passwordRule'),
                minLength: {
                  value: MIN_PASSWORD_LENGTH,
                  message: t('invite.accept.passwordRule'),
                },
                validate: (v) => {
                  if (v.length >= MIN_PASSWORD_LENGTH) {
                    const hasMix = /[a-zA-Z]/.test(v) && (/\d/.test(v) || /[^a-zA-Z0-9]/.test(v));
                    if (!hasMix) {
                      return t('invite.accept.passwordRule');
                    }
                  }
                  return true;
                },
              })}
              error={!!errors.password}
              helperText={passwordRule}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={
                          showPassword
                            ? t('invite.accept.passwordHide')
                            : t('invite.accept.passwordShow')
                        }
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon fontSize="small" />
                        ) : (
                          <VisibilityRoundedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label={t('invite.accept.passwordConfirmLabel')}
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              fullWidth
              size="small"
              {...register('confirmPassword', {
                required: t('invite.accept.passwordRule'),
                validate: (value) => value === password || t('invite.accept.passwordMismatch'),
              })}
              error={!!errors.confirmPassword}
              helperText={confirmRule}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirm((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={
                          showConfirm
                            ? t('invite.accept.passwordHide')
                            : t('invite.accept.passwordShow')
                        }
                      >
                        {showConfirm ? (
                          <VisibilityOffRoundedIcon fontSize="small" />
                        ) : (
                          <VisibilityRoundedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.06) }} />

        <TosSection
          tosAccepted={tosAccepted}
          unfairTermsAccepted={unfairTermsAccepted}
          onTosChange={(checked) => {
            setValue('tosAccepted', checked, { shouldValidate: true });
          }}
          onUnfairChange={(checked) => {
            setValue('unfairTermsAccepted', checked, { shouldValidate: true });
          }}
        />

        <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.06) }} />

        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color: theme.palette.info.contrastText,
                background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
              }}
            >
              <SecurityOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {t('invite.accept.consentHeadline')}
            </Typography>
          </Stack>

          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4, mt: -0.5 }}>
            {t('invite.accept.consentHelper')}
          </Typography>

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentPlatformImprovement}
                  onChange={(e) => setValue('consentPlatformImprovement', e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <Stack spacing={0} sx={{ ml: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                    {t('settings.consent.types.PLATFORM_IMPROVEMENT.label')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', lineHeight: 1.3, fontSize: '0.7rem' }}
                  >
                    {t('invite.accept.consentPlatformImprovementShort')}
                  </Typography>
                </Stack>
              }
              sx={{ alignItems: 'flex-start', mx: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentAnonymizedStatistics}
                  onChange={(e) => setValue('consentAnonymizedStatistics', e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <Stack spacing={0} sx={{ ml: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                    {t('settings.consent.types.ANONYMIZED_STATISTICS.label')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', lineHeight: 1.3, fontSize: '0.7rem' }}
                  >
                    {t('invite.accept.consentAnonymizedStatisticsShort')}
                  </Typography>
                </Stack>
              }
              sx={{ alignItems: 'flex-start', mx: 0 }}
            />
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: 'text.muted', lineHeight: 1.3, fontSize: '0.65rem' }}
          >
            {t('invite.accept.consentLegalNote')}
          </Typography>
        </Stack>

        {submitError && (
          <Typography
            variant="body2"
            role="alert"
            sx={{
              color: 'error.dark',
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              borderRadius: 1.5,
              px: 1.5,
              py: 1,
              fontWeight: 500,
            }}
          >
            {submitError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={!isValid || !tosComplete || setupMutation.isPending}
          startIcon={
            setupMutation.isPending ? (
              <CircularProgress size={18} thickness={5} sx={{ color: 'inherit' }} />
            ) : undefined
          }
          sx={{
            minHeight: 52,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: `0 12px 28px -10px ${alpha(theme.palette.primary.main, 0.55)}`,
          }}
        >
          {t('invite.accept.setupSubmit')}
        </Button>
      </Stack>
    </Box>
  );
}

function getStepTokens(status: StepStatus, theme: Theme) {
  if (status === 'done') {
    return {
      ring: theme.palette.success.main,
      bg: theme.palette.success.main,
      fg: theme.palette.success.contrastText,
      sub: 'success.dark' as const,
    };
  }
  if (status === 'active') {
    return {
      ring: theme.palette.primary.main,
      bg: alpha(theme.palette.primary.main, 0.1),
      fg: theme.palette.primary.main,
      sub: 'primary.dark' as const,
    };
  }
  return {
    ring: alpha(theme.palette.text.primary, 0.18),
    bg: 'transparent' as const,
    fg: theme.palette.text.disabled,
    sub: 'text.muted' as const,
  };
}

function StepBadge({ status, index }: { status: StepStatus; index: number }) {
  const theme = useTheme();
  const tokens = getStepTokens(status, theme);

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        backgroundColor: tokens.bg,
        border: `2px solid ${tokens.ring}`,
        color: tokens.fg,
        fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
        fontWeight: 700,
        fontSize: 14,
        transition: 'all 280ms ease',
      }}
    >
      {status === 'done' && <CheckMark />}
      {status === 'active' && <CircularProgress size={18} thickness={5} color="primary" />}
      {status === 'pending' && index}
      {status === 'active' && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: -4,
            borderRadius: '50%',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            animation: 'invPulse 1.6s ease-in-out infinite',
          }}
        />
      )}
    </Box>
  );
}

function CheckMark() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18 }} aria-hidden>
      <Box
        component="path"
        d="M5 12.5l4.2 4.2L19 7"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        sx={{
          strokeDasharray: 28,
          animation: 'invDrawCheck 360ms ease-out forwards',
        }}
      />
    </Box>
  );
}

function ProgressStep({
  index,
  status,
  title,
  doneLabel,
  showConnector = false,
  connectorActive = false,
}: {
  index: number;
  status: StepStatus;
  title: string;
  doneLabel: string;
  showConnector?: boolean;
  connectorActive?: boolean;
}) {
  const theme = useTheme();
  const tokens = getStepTokens(status, theme);
  const isDone = status === 'done';
  const isActive = status === 'active';
  const isPending = status === 'pending';

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'stretch',
        opacity: isPending ? 0.65 : 1,
        transition: 'opacity 240ms ease',
      }}
    >
      <Stack sx={{ alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ py: 1, display: 'flex', alignItems: 'center' }}>
          <StepBadge status={status} index={index} />
        </Box>
        {showConnector && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 8,
              borderRadius: 1,
              backgroundColor: connectorActive
                ? theme.palette.success.main
                : alpha(theme.palette.text.primary, 0.12),
              transition: 'background-color 280ms ease',
            }}
          />
        )}
      </Stack>
      <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1, justifyContent: 'center', py: 1 }}>
        <Typography
          variant="body1"
          sx={{
            color: isPending ? 'text.muted' : 'text.primary',
            fontWeight: isActive || isDone ? 600 : 500,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {isDone && (
          <Typography variant="caption" sx={{ color: tokens.sub, fontWeight: 600 }}>
            {doneLabel}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function SuccessPanel({ tenantName }: { tenantName?: string }) {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <HeroCard>
      <Box sx={{ p: { xs: 4, sm: 5 }, textAlign: 'center' }}>
        <Stack spacing={3} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              position: 'relative',
              width: 96,
              height: 96,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: `radial-gradient(circle at 30% 30%, ${alpha(theme.palette.success.main, 0.25)}, ${alpha(theme.palette.success.main, 0.05)} 70%)`,
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 8,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                boxShadow: `0 18px 40px -12px ${alpha(theme.palette.success.main, 0.6)}`,
              },
            }}
          >
            <CheckRoundedIcon
              sx={{
                position: 'relative',
                zIndex: 1,
                fontSize: 44,
                color: theme.palette.success.contrastText,
                animation: 'invFadeUp 480ms 120ms cubic-bezier(0.2, 0.8, 0.2, 1) both',
              }}
            />
          </Box>
          <Stack spacing={1}>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.85rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}
            >
              {tenantName
                ? t('invite.accept.successHeadline', { tenantName })
                : t('invite.accept.success')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('invite.accept.successSubhead')}
            </Typography>
          </Stack>
          <RedirectDots />
        </Stack>
      </Box>
    </HeroCard>
  );
}

function RedirectDots() {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={0.75} aria-hidden>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.4),
            animation: `invPulse 1.2s ${i * 0.15}s ease-in-out infinite`,
          }}
        />
      ))}
    </Stack>
  );
}

type ErrorVariant = 'expired' | 'notFound' | 'generic';

const ERROR_ICON_MAP: Record<ErrorVariant, ComponentType<SvgIconProps>> = {
  expired: ReportGmailerrorredRoundedIcon,
  notFound: SearchOffRoundedIcon,
  generic: ReportGmailerrorredRoundedIcon,
};

function ErrorPanel({
  variant,
  customTitle,
  onPrimary,
  primaryLabel,
}: {
  variant: ErrorVariant;
  customTitle?: string;
  onPrimary: () => void;
  primaryLabel: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const Icon = ERROR_ICON_MAP[variant];

  const titleKey =
    variant === 'expired'
      ? 'invite.accept.expiredTitle'
      : variant === 'notFound'
        ? 'invite.accept.notFoundTitle'
        : 'invite.accept.errorTitle';
  const bodyKey =
    variant === 'expired'
      ? 'invite.accept.expiredBody'
      : variant === 'notFound'
        ? 'invite.accept.notFoundBody'
        : 'invite.accept.errorBody';

  const tone = variant === 'expired' ? theme.palette.warning : theme.palette.error;

  return (
    <HeroCard>
      <Box sx={{ p: { xs: 3.5, sm: 5 } }}>
        <Stack spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: alpha(tone.main, 0.12),
              color: tone.dark,
            }}
          >
            <Icon fontSize="medium" />
          </Box>
          <Stack spacing={1}>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.4rem', sm: '1.7rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              {customTitle ?? t(titleKey)}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              {t(bodyKey)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
              width: '100%',
              borderRadius: 2,
              p: 1.5,
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
            }}
          >
            <HelpOutlineRoundedIcon fontSize="small" sx={{ color: 'text.muted' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('invite.accept.needHelp')}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            size="large"
            onClick={onPrimary}
            sx={{
              minHeight: 52,
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: `0 12px 28px -10px ${alpha(theme.palette.primary.main, 0.5)}`,
            }}
          >
            {primaryLabel}
          </Button>
        </Stack>
      </Box>
    </HeroCard>
  );
}

function WrongAccountPanel({
  inviteEmail,
  currentEmail,
  onAccept,
  onCancel,
}: {
  inviteEmail: string;
  currentEmail: string;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <HeroCard>
      <Box sx={{ p: { xs: 3.5, sm: 5 } }}>
        <Stack spacing={3.5}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: theme.palette.warning.dark,
                backgroundColor: alpha(theme.palette.warning.main, 0.15),
              }}
            >
              <PersonOffOutlinedIcon fontSize="small" />
            </Box>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.4, color: 'text.secondary', fontWeight: 600 }}
            >
              {t('invite.accept.wrongAccountEyebrow')}
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.4rem', sm: '1.7rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              {t('invite.accept.wrongAccountHeadline')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              {t('invite.accept.wrongAccountExplain')}
            </Typography>
          </Stack>

          <Stack spacing={1.25}>
            <EmailCompareRow
              label={t('invite.accept.wrongAccountInviteLabel')}
              email={inviteEmail}
              tone="invite"
            />
            <EmailCompareRow
              label={t('invite.accept.wrongAccountCurrentLabel')}
              email={currentEmail}
              tone="current"
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              size="large"
              onClick={onAccept}
              fullWidth
              sx={{
                minHeight: 52,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: `0 12px 28px -10px ${alpha(theme.palette.primary.main, 0.5)}`,
              }}
            >
              {t('invite.accept.signOutAndAccept')}
            </Button>
            <Button
              variant="text"
              size="large"
              onClick={onCancel}
              fullWidth
              sx={{
                minHeight: 52,
                borderRadius: 2,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '1rem',
                color: 'text.secondary',
              }}
            >
              {t('invite.accept.cancel')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </HeroCard>
  );
}

function EmailCompareRow({
  label,
  email,
  tone,
}: {
  label: string;
  email: string;
  tone: 'invite' | 'current';
}) {
  const theme = useTheme();
  const accent = tone === 'invite' ? theme.palette.success.main : theme.palette.error.main;
  const emailColor = tone === 'invite' ? theme.palette.success.dark : theme.palette.text.primary;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'center',
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(accent, 0.25)}`,
        backgroundColor: alpha(accent, 0.05),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: accent,
          flexShrink: 0,
        }}
      />
      <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.muted', fontWeight: 600, letterSpacing: 0.4 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: emailColor,
            fontWeight: 600,
            wordBreak: 'break-all',
          }}
        >
          {email}
        </Typography>
      </Stack>
    </Stack>
  );
}
